/**
 * 案件 1 件から見た業務フロー 1 本（`/pjs/$pjNumber/flows/$flowId`）。
 *
 * 案件を主語にした画面である。案件を選んでから入るので、図の中に案件番号のセレクタは置かない。
 * 案件が画面の一属性に見えると、案件を選ばずにこの画面へ来られる形になり、「案件を選ぶと
 * その案件の差分が読める」という導線が崩れる。
 *
 * **読取は `getFlow`（`flowId`）である。** 骨格を面違いで 2 回引き、差分の印は案件の変更の行
 * （`getChangeHistory`）から、理由は案件の要求（`listPjRequirements`）から取る。
 * 片側が無いこと（案件が新設・削除した業務フロー）は `listPjFlows` の変更の種類から先に知る。
 *
 * **図と詳細の組み立ては `story-flow/` の部品に預ける。** マスターの画面
 * （`FlowPage`）と同じ枠を通すので、「節点を選ぶと右に説明が出る」体験が案件から
 * 入っても変わらない。この画面が持つのは、URL の読み・3 本の取得・状態の振り分けだけである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が触った業務フロー 1 本を、案件を適用した図と選んだ節点の詳細で描く。
 * @graph-connects useFlow [calls] 変更前と変更後の業務フローの取得
 * @graph-connects usePjFlows [calls] 案件が新設・削除した業務フローかの判定
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 * @graph-connects PjStoryFlowView [calls] 差分つきのフローの描画
 */
import type { FlowView, PjFlowRowView } from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { useFlow, usePjFlows } from '../hooks/queries/spec-graph-flow-queries.js';
import { usePjChanges, usePjRequirements } from '../hooks/queries/spec-graph-element-pj-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { resolvePjDeletion } from '../lib/pj-deleted-node.js';
import type { PjAppliedPresence, PjMasterPresence } from './pj-master-presence.js';
import {
  buildStoryFlowPjDiff,
  buildStoryFlowReasons,
  hasStoryFlowPjDiff,
} from './pj-story-flow-diff.js';
import { PJ_DIFF_ROUTE, PJ_FLOW_ROUTE } from './route-paths.js';
import { useCompareSearch } from './flow-workspace/use-compare-search.js';
import { PjStoryFlowView } from './story-flow/PjStoryFlowView.js';
import { readFlowIdParam } from './story-flow/story-flow-params.js';

/**
 * 片側が未存在だったときに置く空の骨格。
 *
 * 図に要るのはレーン・節点・枝の 3 つだけで、名前や更新時刻は左右比較の図に出ない。相手側の
 * 姿から名前だけ借りた偽の「変更前」を組むと、存在しなかったものが存在したかのように読める。
 * 空で置けば「何も無かった」がそのまま図になる。
 *
 * @param source 相手側の骨格。名前と型だけを写す
 * @returns 節点も枝もレーンも無い骨格
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 片側が未存在のときに置く、節点も枝も無い骨格を組む。
 * @graph-connects none
 */
function emptyFlowLike(source: FlowView): FlowView {
  return { ...source, lanes: [], nodes: [], transitions: [] };
}

/** 案件が変えた業務フローの行から読んだ、両面の有無。 */
export interface PjFlowPresence {
  /** マスター（変更前）に居るか */
  master: PjMasterPresence;
  /** 案件を重ねた姿（変更後）に居るか */
  applied: PjAppliedPresence;
}

/**
 * 案件が変えた業務フローの行から、変更前・変更後にその業務フローが居るかを決める。
 *
 * 案件が新設した業務フロー（`added`）は変更前に居ない。削除した業務フロー（`deleted`）は変更後に
 * 居ない。行が無い・更新だけなら両面に居る。行がまだ届いていなければ決めない（`unknown`）。
 *
 * @param rows 案件が変えた業務フローの行。取得前なら undefined
 * @param flowId 業務フローの id
 * @returns 両面の有無
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件が変えた業務フローの行から、変更前と変更後にその業務フローが居るかを決める。
 * @graph-connects none
 */
export function pjFlowPresenceOf(
  rows: readonly PjFlowRowView[] | undefined,
  flowId: number,
): PjFlowPresence {
  if (rows === undefined) return { master: 'unknown', applied: 'unknown' };
  const change = rows.find((row) => row.flowId === flowId)?.change;
  return {
    master: change === 'added' ? 'absent' : 'present',
    applied: change === 'deleted' ? 'absent' : 'present',
  };
}

/**
 * 案件から見たフローを、URL の誤り・取得中・未存在・失敗・本体の 5 状態で描く。
 *
 * @returns 案件から見た業務フロー
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件から見た業務フローを、URL の誤り・取得中・未存在・失敗・本体の 5 状態で描く。
 * @graph-connects readFlowIdParam [calls] URL から業務フローの id と案件番号を読む
 * @graph-connects PjFlowBody [calls] id が読めたあとの取得と状態の振り分け
 */
export function PjFlowPage() {
  const { pjNumber, flowId } = useParams({ from: PJ_FLOW_ROUTE });
  const id = readFlowIdParam(flowId);
  const pj = readFlowIdParam(pjNumber);

  if (id === null || pj === null) {
    return <PjFlowNotFound id={flowId} pjNumber={pjNumber} />;
  }
  return <PjFlowBody flowId={id} pjNumber={pj} rawId={flowId} />;
}

/** `PjFlowBody` の props。 @graph-connects none */
interface PjFlowBodyProps {
  /** 読取へ渡す業務フローの id。 */
  flowId: number;
  /** 読取へ渡す案件番号。 */
  pjNumber: number;
  /** URL に書かれていた業務フローの綴り。未存在の案内に出す。 */
  rawId: string;
}

/**
 * id が読めたあとの 3 本の取得と、状態の振り分け。
 *
 * **id の関門（`PjFlowPage`）から分けてある。** 関門で弾いた綴りは読取へ渡せない一方、
 * フックは条件付きで呼べない。分ければ、読取を呼ぶ側は必ず正しい id を持っている。
 *
 * @param props 業務フローの id・案件番号・URL の綴り
 * @returns 取得中・未存在・失敗・本体のいずれか
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローを面違いで 2 回引き、案件の変更と要求を揃えて差分つきの図を描く。
 * @graph-connects useFlow [calls] 変更前と変更後の業務フローの取得
 * @graph-connects usePjFlows [calls] 案件が変えた業務フローの一覧の取得
 * @graph-connects usePjChanges [calls] 案件の変更の行の取得
 * @graph-connects usePjRequirements [calls] 案件の要求の取得
 * @graph-connects buildStoryFlowReasons [calls] 要求から業務ごとの理由への畳み込み
 * @graph-connects resolvePjDeletion [calls] 変更後の未存在が案件の削除かの判定
 * @graph-connects pjFlowPresenceOf [calls] 案件が新設・削除した業務フローかの判定
 */
function PjFlowBody({ flowId, pjNumber, rawId }: PjFlowBodyProps) {
  const { t } = useTranslation();
  /*
   * 同じ読取を面違いで 2 回引く。`before` はこの案件が骨格を変える直前、`after` はこの案件の
   * 未確定の変更を重ねた姿である。書込は案件の層へ積むだけでマスターを動かさないので、確定前の
   * 案件では変更後がマスターに無く、面を指定しないと右側に変更前と同じ図が出る。Query キーに
   * 案件番号と面が入っているので 2 つは別のキャッシュに乗る。
   */
  const changes = usePjChanges(pjNumber);
  const requirements = usePjRequirements(pjNumber);
  const pjFlows = usePjFlows(pjNumber);

  /*
   * 差分と理由は入力が変わったときだけ導出する。節点を選ぶたびに導出し直すと、中身が同じでも
   * 参照が変わるため図が節点を丸ごと組み直し、選んだ瞬間にちらつく。
   */
  const changeRows = changes.data?.changes;
  const requirementRows = requirements.data?.requirements;

  /*
   * **案件が新しく作った業務フローには変更前が無いので、引かない。** 引けば必ず未存在で返り、
   * 押していない利用者のコンソールと通信の記録に 404 が 1 本残る。案件が作ったかどうかは
   * `listPjFlows` の変更の種類に書いてあるので、揃ってから決める。
   *
   * **逆に案件が消す業務フローには変更後が無いので、同じ理由で変更後を引かない。**
   */
  const presence = pjFlowPresenceOf(pjFlows.data?.flows, flowId);
  const masterPresence = presence.master;
  const appliedPresence = presence.applied;
  const after = useFlow(flowId, { number: pjNumber, side: 'after' }, appliedPresence === 'present');
  const before = useFlow(
    flowId,
    { number: pjNumber, side: 'before' },
    masterPresence === 'present',
  );
  const reasons = useMemo(() => buildStoryFlowReasons(requirementRows ?? []), [requirementRows]);

  /*
   * **変更後の未存在は、変更前に居れば案件がフローごと消したことを言う。** 未存在の画面に
   * 落とすと、案件の変更一覧から削除行を開いた読み手は消えた図を見られず、削除が意図どおりかを
   * 確かめられない。変更前だけに図がある差分として描く。変更の行から先に分かった（引かなかった）
   * 場合も同じ振り分けに畳む。
   */
  const deletion = resolvePjDeletion({
    pjMode: true,
    afterError: after.error,
    afterKnownMissing: appliedPresence === 'absent',
    before,
  });
  if (deletion.kind === 'beforeFailed') return <ErrorState error={deletion.error} />;
  if (deletion.kind === 'absent') return <PjFlowNotFound id={rawId} pjNumber={String(pjNumber)} />;
  const afterMissing = deletion.kind === 'deleted';

  if (deletion.kind === 'present' && after.error !== null && after.error !== undefined) {
    return <ErrorState error={after.error} />;
  }

  /*
   * **変更前の未存在は失敗ではない。** 案件が新しく作った業務フローには変更前が無い。空の骨格を
   * 変更前として置けば、「何も無かったところに全部が足された」が差分としてそのまま正しく出る。
   *
   * 未存在は 2 通りの分かり方をする — 変更の行から先に分かる（引かずに済む）場合と、マスターを
   * 引いて未存在で返る場合である。**両方を同じ「変更前が無い」へ畳む。** 畳まないと、引かずに
   * 済ませた面だけが変更前の到着を永久に待つ。
   */
  const beforeMissing = masterPresence === 'absent' || isNotFoundError(before.error);
  if (!beforeMissing && before.error !== null && before.error !== undefined) {
    return <ErrorState error={before.error} />;
  }
  if (changes.error !== null && changes.error !== undefined) {
    return <ErrorState error={changes.error} />;
  }
  if (pjFlows.error !== null && pjFlows.error !== undefined) {
    return <ErrorState error={pjFlows.error} />;
  }
  /*
   * **要求の失敗も失敗として出す。** 理由が空であることには 2 つの意味があり、「要求に結ばれて
   * いない変更だった」と「要求が読めなかった」は画面の上で見分けがつかない。握り潰すと、読み手は
   * 後者を前者として読み、「この案件はこの業務を変えた理由を記録していない」という嘘を受け取る。
   * 変更の行と取得の形が対称なので、倒し方も揃える。
   */
  if (requirements.error !== null && requirements.error !== undefined) {
    return <ErrorState error={requirements.error} />;
  }

  const afterFlow =
    afterMissing && before.data !== undefined ? emptyFlowLike(before.data) : after.data;
  if (afterFlow === undefined || (!beforeMissing && before.data === undefined)) {
    return <PjFlowSkeleton label={t('common.loading')} />;
  }
  /*
   * **変更の行と要求が揃うまで描かない。** 2 本は互いに依存しない別々の取得なので、要求だけが
   * 遅れて返る窓がある。その窓で描くと、理由の無い図を一度出してから理由が後から乗る。読み手に
   * とってそれは「この案件はこの業務を変えた理由を記録していない」と読める — 失敗を握り潰した
   * ときと同じ嘘を、取得中というだけで出すことになる。空の理由が持つ 2 つの意味を区別するという
   * 判断（上の失敗の扱い）は、取得中にも同じく効かせる。
   */
  if (changeRows === undefined || requirementRows === undefined) {
    return <PjFlowSkeleton label={t('common.loading')} />;
  }

  const beforeFlow = before.data ?? emptyFlowLike(afterFlow);

  return (
    <PjFlowDiagram
      pjNumber={pjNumber}
      beforeFlow={beforeFlow}
      afterFlow={afterFlow}
      afterMissing={afterMissing}
      changeRows={changeRows}
      reasons={reasons}
    />
  );
}

/** `PjFlowDiagram` の props。 @graph-connects none */
interface PjFlowDiagramProps {
  /** いま適用している案件番号。 */
  pjNumber: number;
  /** 案件が骨格を変える直前の姿。 */
  beforeFlow: FlowView;
  /** 案件を適用した姿。 */
  afterFlow: FlowView;
  /** この案件が業務フローごと消したか。 */
  afterMissing: boolean;
  /** 案件の変更の行。 */
  changeRows: NonNullable<ReturnType<typeof usePjChanges>['data']>['changes'];
  /** 業務の id ごとの理由。 */
  reasons: ReadonlyMap<number, readonly string[]>;
}

/**
 * 取得が揃ったあとの差分の導出と描画。
 *
 * **取得と分岐（`PjFlowBody`）から分けてある。** 差分の導出は取得が揃ってからでないと
 * 走らせられない一方、結果は**選ぶ節点をまたいで同じものを使い続けたい**。取得側は途中で
 * 打ち切る分岐を何本も持つので、その後ろでは記憶する仕組み（`useMemo`）を置けない。分ければ
 * 打ち切りの無いところで記憶できる。
 *
 * @param props 案件番号・変更前後の骨格・未存在の旗・変更の行・理由
 * @returns 差分つきのフロー 1 本
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 揃った骨格と変更の行から差分を導出し、案件から見たフローを描く。
 * @graph-connects buildStoryFlowPjDiff [calls] 変更前後の突き合わせ
 * @graph-connects hasStoryFlowPjDiff [calls] 図に変更前との差が有るかの判定
 * @graph-connects PjStoryFlowView [calls] 差分つきのフローの描画
 * @graph-connects useCompareSearch [calls] URL が持つ左右比較の開閉
 */
function PjFlowDiagram({
  pjNumber,
  beforeFlow,
  afterFlow,
  afterMissing,
  changeRows,
  reasons,
}: PjFlowDiagramProps) {
  const diff = useMemo(
    () => buildStoryFlowPjDiff(beforeFlow, afterFlow, changeRows),
    [beforeFlow, afterFlow, changeRows],
  );
  /*
   * 左右比較の開閉は URL が持つ。既定は 2 面で、閉じたときだけ `?compare=0` が載る。図に差が
   * 無ければ URL に関係なく 1 面で、比較のボタンは押せない。
   */
  const compare = useCompareSearch(hasStoryFlowPjDiff(diff));

  return (
    <PjStoryFlowView
      pjNumber={pjNumber}
      before={beforeFlow}
      after={afterFlow}
      afterMissing={afterMissing}
      diff={diff}
      reasons={reasons}
      split={compare.split}
      compareDisabled={compare.disabled}
      onToggleSplit={compare.toggle}
    />
  );
}

/** `PjFlowSkeleton` の props。 @graph-connects none */
interface PjFlowSkeletonProps {
  /** 読み上げに出す取得中の名前。 */
  label: string;
}

/**
 * 取得中の骨組み。
 *
 * @param props 読み上げに出す名前
 * @returns 取得中の骨組み
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件から見たフローの取得中を骨組みで描く。
 * @graph-connects none
 */
function PjFlowSkeleton({ label }: PjFlowSkeletonProps) {
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label={label}>
      <Skeleton variant="rectangular" height="4rem" />
      <Skeleton variant="rectangular" height="24rem" />
    </div>
  );
}

/** `PjFlowNotFound` の props。 @graph-connects none */
interface PjFlowNotFoundProps {
  /** URL に書かれていた業務フローの綴り。 */
  id: string;
  /** URL に書かれていた案件の綴り。戻り先に使う。 */
  pjNumber: string;
}

/**
 * 業務フローが見つからないときの案内。
 *
 * 旧 URL（`/pjs/$pjNumber/flows/$usId`）は雛形が同じなので、旧ストーリーの番号がそのまま業務
 * フローの番号として読まれてここへ来る。戻り先を案件の変更一覧にして、そこから辿り直せるようにする。
 *
 * @param props 業務フローと案件の綴り
 * @returns 未存在の案内
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローが見つからないことを、案件の変更一覧への戻り口つきで案内する。
 * @graph-connects none
 */
function PjFlowNotFound({ id, pjNumber }: PjFlowNotFoundProps) {
  const { t } = useTranslation();
  return (
    <NotFoundState
      id={id}
      action={
        <Link
          to={PJ_DIFF_ROUTE}
          params={{ pjNumber }}
          className="text-sm text-[var(--color-accent-primary)] underline"
        >
          {t('pjFlow.backToPj')}
        </Link>
      }
    />
  );
}
